import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { ChevronRight, Menu } from "lucide-react";
import { Fragment } from "react";

import { agentApiClient, profileApiClient, programApiClient } from "@/libs/api";

import { AuthButton } from "./auth-button";

const SECTION_LABELS: Record<string, string> = {
  home: "Dashboard",
  candidates: "Ứng viên",
  programs: "Chương trình",
  applications: "Ứng viên",
  commissions: "Hoa hồng",
  tree: "Cây đại lý",
  profile: "Hồ sơ"
};

type BreadcrumbItem = { label: string; to?: string };

interface TopBarProps {
  onMenuClick: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { pathname } = useLocation();
  const segments = pathname.split("/").filter(Boolean);
  const section = segments[0] ?? "";
  const resourceId = segments[1];

  const candidateQuery = useQuery({
    queryKey: ["candidate-profile", resourceId ?? ""],
    queryFn: () => profileApiClient.getProfileByUserId(resourceId!),
    enabled: section === "candidates" && !!resourceId,
    staleTime: 2 * 60 * 1000
  });

  const programQuery = useQuery({
    queryKey: ["program-detail", resourceId ?? ""],
    queryFn: () => programApiClient.getOne(resourceId!),
    enabled: section === "programs" && !!resourceId,
    staleTime: 5 * 60 * 1000
  });

  const applicationQuery = useQuery({
    queryKey: ["agent-application", resourceId ?? ""],
    queryFn: () => agentApiClient.getApplicationById(resourceId!),
    enabled: section === "applications" && !!resourceId,
    staleTime: 2 * 60 * 1000
  });

  const treeAgentQuery = useQuery({
    queryKey: ["tree-agent-profile", resourceId ?? ""],
    queryFn: () => profileApiClient.getProfileByUserId(resourceId!),
    enabled: section === "tree" && !!resourceId,
    staleTime: 2 * 60 * 1000
  });

  const breadcrumb: BreadcrumbItem[] = (() => {
    const parentLabel = SECTION_LABELS[section] ?? section;

    if (!resourceId) {
      return [{ label: parentLabel }];
    }

    let resourceLabel = "...";
    let parentTo = `/${section}`;

    if (section === "candidates" && candidateQuery.data) {
      resourceLabel = candidateQuery.data.name;
    } else if (section === "programs" && programQuery.data) {
      resourceLabel = programQuery.data.title ?? "-";
    } else if (section === "applications") {
      parentTo = "/candidates";
      if (applicationQuery.data) resourceLabel = applicationQuery.data.program.title ?? "-";
    } else if (section === "tree" && treeAgentQuery.data) {
      resourceLabel = treeAgentQuery.data.name ?? treeAgentQuery.data.email ?? "...";
    }

    return [{ label: parentLabel, to: parentTo }, { label: resourceLabel }];
  })();

  return (
    <header className="flex items-center justify-between flex-shrink-0 mt-1 mb-2">
      <nav className="flex items-center gap-1.5">
        <button onClick={onMenuClick} className="md:hidden mr-1 text-slate-500 hover:text-primary-700">
          <Menu size={20} />
        </button>
        {breadcrumb.map((item, i) => (
          <Fragment key={i}>
            {i > 0 && <ChevronRight size={13} className="text-slate-400" />}
            {item.to ? (
              <Link
                to={item.to}
                className="text-responsive-md text-slate-500 hover:text-primary-700 transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-responsive-lg font-semibold text-primary-700">{item.label}</span>
            )}
          </Fragment>
        ))}
      </nav>
      <AuthButton />
    </header>
  );
}
